import { IconCirclePlus, IconX } from '@tabler/icons-react-native'
import { compact, map } from 'lodash'
import { type FC } from 'react'

import { type PromptSelectInput_query$key } from '@/__generated__/PromptSelectInput_query.graphql'
import { Box, Pressable, PromptSelectInput, Text, TextInput, VStack } from '@/components'
import { COLOR_WHITE, hasUrl, PROFILE_PROMPT_ANSWER_MAX, PROFILE_PROMPTS_MAX } from '@/lib'

export type PromptSlot = {
  promptId: string | null
  answer: string
  lockedQuestion?: string
}

export interface GetToKnowMeEditorProps {
  queryRef: PromptSelectInput_query$key | null | undefined
  value: PromptSlot[]
  onChange: (next: PromptSlot[]) => void
  isDisabled?: boolean
  testIDPrefix?: string
}

export const GetToKnowMeEditor: FC<GetToKnowMeEditorProps> = ({
  queryRef,
  value,
  onChange,
  isDisabled,
  testIDPrefix
}) => {
  const selectedIds = compact(map(value, 'promptId'))

  const updateSlot = (index: number, patch: Partial<PromptSlot>) =>
    onChange(map(value, (slot, i) => (i === index ? { ...slot, ...patch } : slot)))

  const removeSlot = (index: number) => onChange(value.filter((_, i) => i !== index))

  const addSlot = () => onChange([...value, { promptId: null, answer: '' }])

  return (
    <VStack className='gap-4'>
      {map(value, (slot, index) =>
        slot.lockedQuestion ? (
          <VStack
            className='gap-2 rounded-lg bg-white/10 p-4'
            key={slot.promptId ?? index}
          >
            <Box className='flex-row items-center justify-between'>
              <Text className='font-poppinsMedium color-white'>{slot.lockedQuestion}</Text>
              <Pressable
                accessibilityLabel={`Remove prompt ${index + 1}`}
                disabled={isDisabled}
                onPress={() => removeSlot(index)}
                testID={testIDPrefix ? `${testIDPrefix}.removeButton-${index}` : undefined}
              >
                <IconX
                  color={COLOR_WHITE}
                  size={20}
                />
              </Pressable>
            </Box>
            <Text className='font-poppins text-xs color-white/50'>This prompt was removed by an admin</Text>
            <Text className='font-poppins color-white'>{slot.answer}</Text>
          </VStack>
        ) : (
          <VStack
            className='gap-2 rounded-lg bg-white/10 p-4'
            key={slot.promptId ?? index}
          >
            <Box className='flex-row items-center justify-between'>
              <Text className='font-poppinsMedium color-white'>Prompt {index + 1}</Text>
              <Pressable
                accessibilityLabel={`Remove prompt ${index + 1}`}
                disabled={isDisabled}
                onPress={() => removeSlot(index)}
                testID={testIDPrefix ? `${testIDPrefix}.removeButton-${index}` : undefined}
              >
                <IconX
                  color={COLOR_WHITE}
                  size={20}
                />
              </Pressable>
            </Box>

            <PromptSelectInput
              accessibilityLabel={`Prompt ${index + 1}`}
              excludeIds={selectedIds}
              isDisabled={isDisabled}
              onChange={(promptId) => updateSlot(index, { promptId })}
              placeholder='Choose a prompt'
              queryRef={queryRef}
              testID={testIDPrefix ? `${testIDPrefix}.promptSelect-${index}` : undefined}
              value={slot.promptId}
            />

            <TextInput
              accessibilityLabel={`Answer for prompt ${index + 1}`}
              error={hasUrl(slot.answer) ? 'Answers cannot contain links' : undefined}
              inputFieldProps={{
                maxLength: PROFILE_PROMPT_ANSWER_MAX,
                placeholder: 'Your answer',
                testID: testIDPrefix ? `${testIDPrefix}.answerInput-${index}` : undefined
              }}
              isDisabled={isDisabled || !slot.promptId}
              onChange={(answer) => updateSlot(index, { answer })}
              value={slot.answer}
            />
          </VStack>
        )
      )}

      {value.length < PROFILE_PROMPTS_MAX && (
        <Pressable
          accessibilityLabel='Add a prompt'
          className='flex-row items-center justify-center gap-2 rounded-lg bg-white/10 p-4'
          disabled={isDisabled}
          onPress={addSlot}
          testID={testIDPrefix ? `${testIDPrefix}.addButton` : undefined}
        >
          <IconCirclePlus
            color={COLOR_WHITE}
            size={20}
          />
          <Text className='font-poppinsMedium color-white'>Add</Text>
        </Pressable>
      )}
    </VStack>
  )
}
